// The on-screen keyboard (phones, tablets), for shared pieces that must not
// fight it. Nothing happens on a screen without one.

/** The visible part shrank by more than this: an on-screen keyboard is up. */
const KEYBOARD_MIN_PX = 120;
/** The longest wait for the keyboard to finish going down. */
export const KEYBOARD_CLOSE_WAIT_MS = 600;

/** Whether an on-screen keyboard is up now. */
export function keyboardUp(): boolean {
  const vv = typeof window === "undefined" ? null : window.visualViewport;
  return (
    vv != null &&
    vv.height < document.documentElement.clientHeight - KEYBOARD_MIN_PX
  );
}

/** A place that brings up the keyboard when it has the focus. */
export function isTextEntry(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable || el.tagName === "TEXTAREA") return true;
  if (el.tagName !== "INPUT") return false;
  const type = (el as HTMLInputElement).type;
  return !["checkbox", "radio", "button", "submit", "file", "range"].includes(
    type,
  );
}

/**
 * Takes the keyboard down (the field loses the focus) and calls `done` once it
 * is down, or after KEYBOARD_CLOSE_WAIT_MS. At once when none is up.
 */
export function afterKeyboardDown(done: () => void): () => void {
  const vv = window.visualViewport;
  const active = document.activeElement;
  if (!vv || !keyboardUp()) {
    done();
    return () => {};
  }
  if (isTextEntry(active)) active.blur();
  let over = false;
  const finish = () => {
    if (over) return;
    over = true;
    vv.removeEventListener("resize", onResize);
    window.clearTimeout(timer);
    done();
  };
  const onResize = () => {
    if (!keyboardUp()) finish();
  };
  vv.addEventListener("resize", onResize);
  const timer = window.setTimeout(finish, KEYBOARD_CLOSE_WAIT_MS);
  return () => {
    over = true;
    vv.removeEventListener("resize", onResize);
    window.clearTimeout(timer);
  };
}
